import React, { createContext, useContext, useState, useEffect } from 'react';

interface UpsellContextType {
  // Modal state
  isUpsellModalOpen: boolean;
  openUpsellModal: (triggerType: 'truck_limit' | 'analytics' | 'general') => void;
  closeUpsellModal: () => void;
  
  // Current trigger
  currentTrigger: 'truck_limit' | 'analytics' | 'general' | null;
  
  // Usage limits
  usageLimits: {
    trucks: { current: number; limit: number };
    analytics: { current: number; limit: number };
  };
  
  // Update usage
  updateUsage: (type: 'trucks' | 'analytics', increment?: number) => void;
  
  // Check if limit reached
  isLimitReached: (type: 'trucks' | 'analytics') => boolean;
  
  // Onboarding
  showOnboarding: boolean;
  completeOnboarding: () => void;
  
  // User role
  userRole: 'vendor' | 'user' | 'admin';
  
  // Actions
  handleUpgrade: (plan: 'premium' | 'business') => void;
}

const UpsellContext = createContext<UpsellContextType | undefined>(undefined);

export const useUpsell = () => {
  const context = useContext(UpsellContext);
  if (!context) {
    throw new Error('useUpsell must be used within an UpsellProvider');
  }
  return context;
};

interface UpsellProviderProps {
  children: React.ReactNode;
  userRole: 'vendor' | 'user' | 'admin';
}

export const UpsellProvider: React.FC<UpsellProviderProps> = ({ 
  children, 
  userRole 
}) => {
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const [currentTrigger, setCurrentTrigger] = useState<'truck_limit' | 'analytics' | 'general' | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // Usage limits based on user role
  const [usageLimits, setUsageLimits] = useState({
    trucks: { 
      current: 0, 
      limit: userRole === 'vendor' ? 10 : 0 
    },
    analytics: { 
      current: 0, 
      limit: userRole === 'vendor' ? 50 : 0 
    }
  });

  // Check if user should see onboarding - vendors only
  useEffect(() => {
    try {
      const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
      const isNewUser = localStorage.getItem('isNewUser') === 'true';
      
      // Show onboarding only for vendor users (not general users)
      if ((!hasSeenOnboarding || isNewUser) && userRole === 'vendor') {
        setShowOnboarding(true);
      }
    } catch (error) {
      console.warn('Failed to read localStorage for onboarding:', error);
    }
  }, [userRole]);

  const openUpsellModal = (triggerType: 'truck_limit' | 'analytics' | 'general') => {
    setCurrentTrigger(triggerType);
    setIsUpsellModalOpen(true);
  };

  const closeUpsellModal = () => {
    setIsUpsellModalOpen(false);
    setCurrentTrigger(null);
  };

  const updateUsage = (type: 'trucks' | 'analytics', increment = 1) => {
    setUsageLimits(prev => {
      const newCurrent = prev[type].current + increment;
      const limitReached = newCurrent >= prev[type].limit;
      
      // Check if limit reached and trigger upsell
      if (limitReached) {
        const triggerType = type === 'trucks' ? 'truck_limit' : 'analytics';
        // Auto-open modal for critical limits
        if (type === 'trucks') {
          openUpsellModal(triggerType);
        }
      }
      
      return {
        ...prev,
        [type]: {
          ...prev[type],
          current: newCurrent
        }
      };
    });
  };

  const isLimitReached = (type: 'trucks' | 'analytics') => {
    return usageLimits[type].current >= usageLimits[type].limit;
  };

  const completeOnboarding = () => {
    setShowOnboarding(false);
    try {
      localStorage.setItem('hasSeenOnboarding', 'true');
      localStorage.removeItem('isNewUser'); // Clear new user flag
    } catch (error) {
      console.warn('Failed to persist onboarding completion:', error);
    }
  };

  const handleUpgrade = (plan: 'premium' | 'business') => {
    // In a real app, this would redirect to payment or handle the upgrade
    setTimeout(() => {
      closeUpsellModal();
    }, 1000);
  };

  const value: UpsellContextType = {
    isUpsellModalOpen,
    openUpsellModal,
    closeUpsellModal,
    currentTrigger,
    usageLimits,
    updateUsage,
    isLimitReached,
    showOnboarding,
    completeOnboarding,
    userRole,
    handleUpgrade
  };

  return (
    <UpsellContext.Provider value={value}>
      {children}
    </UpsellContext.Provider>
  );
};